import React, { useState, useEffect } from 'react';
import './App.css';
import logoImg from './image/web-logo.png';

const API_URL = 'https://twillioapi.website-design-india.com/api';

function App() {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [users, setUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    twilioNumber: '',
    twilioAccount: ''
  });
  const [twilioAccounts, setTwilioAccounts] = useState([]);
  const [showTwilioModal, setShowTwilioModal] = useState(false);
  const [twilioForm, setTwilioForm] = useState({ name: '', accountSid: '', authToken: '', phoneNumbers: '' });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({}); // map userId -> bool

  // initialize visiblePasswords from localStorage so toggles persist across reloads
  useEffect(() => {
    try {
      const raw = localStorage.getItem('visiblePasswords');
      if (raw) setVisiblePasswords(JSON.parse(raw));
    } catch (e) {
      // ignore parse errors
    }
  }, []);

  // persist visiblePasswords to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('visiblePasswords', JSON.stringify(visiblePasswords));
    } catch (e) {}
  }, [visiblePasswords]);

  useEffect(() => {
    if (token) {
      fetchUsers();
      fetchTwilioAccounts();
    }
  }, [token]);

  const fetchTwilioAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/twilio-accounts`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to fetch accounts');
      setTwilioAccounts(data.accounts || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        const text = await response.text();
        throw new Error('Server returned non-JSON response: ' + (text.length > 500 ? text.slice(0,500) + '...' : text));
      }

      if (!data.success) {
        throw new Error(data.error || 'Login failed');
      }

      localStorage.setItem('adminToken', data.token);
      setToken(data.token);
      setSuccess('Login successful!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setUsers([]);
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!data.success) throw new Error(data.error);
      setUsers(data.users);
      // If backend returns decrypted plaintext passwords, mark them visible by default
      const visFromBackend = {};
      (data.users || []).forEach(u => {
        if (u && u.id && u.password) visFromBackend[String(u.id)] = true;
      });
      // merge with any existing persisted toggles
      setVisiblePasswords(prev => ({ ...prev, ...visFromBackend }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userForm)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create user');
      }

      // API intentionally doesn't return plaintext passwords for security.
      // Keep the plaintext password in UI immediately after creation so admin can see it.
      const createdUser = { ...data.user, password: userForm.password };
      setUsers([...users, createdUser]);
      if (createdUser.id) setVisiblePasswords(prev => ({ ...prev, [String(createdUser.id)]: true }));
      setShowCreateModal(false);
      setUserForm({ username: '', password: '', twilioNumber: '', twilioAccount: '' });
      setSuccess('User created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userForm)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update user');
      }

      // Preserve plaintext password in the UI if admin provided one during update
      const updatedUser = { ...data.user };
      if (userForm.password) updatedUser.password = userForm.password;
      setUsers(users.map(u => u.id === editingUser.id ? updatedUser : u));
      if (userForm.password) setVisiblePasswords(prev => ({ ...prev, [String(editingUser.id)]: true }));
      setEditingUser(null);
      setUserForm({ username: '', password: '', twilioNumber: '', twilioAccount: '' });
      setSuccess('User updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${API_URL}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!data.success) throw new Error(data.error);

      setUsers(users.filter(u => u.id !== userId));
      setSuccess('User deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      password: '',
      twilioNumber: user.twilioNumber || '',
      twilioAccount: user.twilioAccount ? user.twilioAccount.id : ''
    });
    setError('');
  };


  const closeModal = () => {
    setShowCreateModal(false);
    setEditingUser(null);
    setUserForm({ username: '', password: '', twilioNumber: '', twilioAccount: '' });
    setError('');
  };

  const togglePasswordVisibility = (userId) => {
    const key = String(userId);

    // If password already present for the user, just toggle visibility
    const user = users.find(u => String(u.id) === key);
    if (user && user.password) {
      setVisiblePasswords(prev => ({ ...prev, [key]: !prev[key] }));
      return;
    }

    // Otherwise, attempt to request the decrypted password from backend (if supported)
    (async () => {
      try {
        const res = await fetch(`${API_URL}/admin/users/${key}/reveal`, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (!data.success || !data.password) throw new Error(data.error || 'Password not available');
        // update user in list with returned password and mark visible
        setUsers(prev => prev.map(u => (String(u.id) === key ? { ...u, password: data.password } : u)));
        setVisiblePasswords(prev => ({ ...prev, [key]: true }));
      } catch (err) {
        setError(err.message || 'Failed to retrieve password');
        setTimeout(() => setError(''), 3000);
      }
    })();
  };

  const selectedAccount = twilioAccounts.find(a => String(a.id) === String(userForm.twilioAccount));
  const availableNumbers = selectedAccount ? (selectedAccount.phoneNumbers || []).filter(n => {
    return !users.find(u => {
      if (!u.twilioNumber) return false;
      const acct = u.twilioAccount;
      const acctId = acct ? (acct.id ? String(acct.id) : String(acct)) : '';
      return u.twilioNumber === n && acctId === String(selectedAccount.id);
    });
  }) : [];

  const handleCreateTwilioAccount = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const body = {
        name: twilioForm.name,
        accountSid: twilioForm.accountSid,
        authToken: twilioForm.authToken,
        phoneNumbers: twilioForm.phoneNumbers ? twilioForm.phoneNumbers.split(',').map(s => s.trim()).filter(Boolean) : []
      };
      const res = await fetch(`${API_URL}/admin/twilio-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to create account');
      setTwilioForm({ name: '', accountSid: '', authToken: '', phoneNumbers: '' });
      await fetchTwilioAccounts();
      setSuccess('Twilio account created');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTwilioAccount = async (id) => {
    if (!window.confirm('Delete this Twilio account?')) return;
    try {
      const res = await fetch(`${API_URL}/admin/twilio-accounts/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Delete failed');
      await fetchTwilioAccounts();
      setSuccess('Twilio account deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!token) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="logo">👨‍💼</div>
          <h1>Admin Panel</h1>
          <p className="subtitle">User Management System</p>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                placeholder="Enter username"
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Enter password"
                required
              />
            </div>
            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <p className="hint">Default: admin / admin123</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          {/* <h1>📞 Calling App Admin</h1> */}
          <img src={logoImg} alt='Admin Avatar' className="avatar" />
          <span className="badge">{users.length} Users</span>
        </div>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </header>

      <div className="container">
        {(error || success) && (
          <div className="notification-area">
            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}
          </div>
        )}

        <div className="top-bar">
          <div>
            <h2>Manage Users</h2>
            <p className="subtitle-text">Create and assign Twilio numbers to users</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary">
              + Add New User
            </button>
            <button onClick={() => setShowTwilioModal(true)} className="btn-secondary">
              Manage Twilio Accounts
            </button>
          </div>
        </div>

        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Password</th>
                <th>Twilio Number</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-state">
                    <div className="empty-icon">📭</div>
                    <p>No users yet. Create your first user!</p>
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id}>
                    <td><strong>{index + 1}</strong></td>
                    <td>{user.username}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{(visiblePasswords[String(user.id)]) ? (user.password || '') : (user.password ? '••••••' : '')}</span>
                        {user.password && (
                          <button type="button" className="eye-btn" onClick={() => togglePasswordVisibility(user.id)}>
                            {visiblePasswords[String(user.id)] ? 'Hide' : 'Show'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      {user.twilioNumber ? (
                        <span className="phone-number">{user.twilioNumber}</span>
                      ) : (
                        <span className="not-assigned">Not assigned</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? '✓ Active' : '✗ Inactive'}
                      </span>
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => openEditModal(user)} className="btn-edit">
                          Edit
                        </button>
                        <button onClick={() => handleDeleteUser(user.id)} className="btn-delete">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(showCreateModal || editingUser) && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingUser ? '✏️ Edit User' : '➕ Create New User'}</h2>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  placeholder="Enter username"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password {editingUser && '(leave empty to keep current)'}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type={showPasswordInput ? 'text' : 'password'}
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder="Enter password"
                    required={!editingUser}
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="eye-btn" onClick={() => setShowPasswordInput(!showPasswordInput)}>
                    {showPasswordInput ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Twilio Account</label>
                <select
                  value={userForm.twilioAccount}
                  onChange={(e) => setUserForm({ ...userForm, twilioAccount: e.target.value, twilioNumber: '' })}
                >
                  <option value="">-- None --</option>
                  {twilioAccounts.map(a => (
                    <option key={a.id} value={String(a.id)}>{a.name} ({a.accountSid})</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Twilio Number</label>
                <select className="phone-select"
                  value={userForm.twilioNumber}
                  onChange={(e) => setUserForm({ ...userForm, twilioNumber: e.target.value })}
                >
                  <option className="phone-select" value="">-- None --</option>
                  {availableNumbers.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <small className="hint-text">Choose a number from the selected Twilio account, or leave empty.</small>
              </div>
              {error && <div className="error">{error}</div>}
              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-primary">
                  {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTwilioModal && (
        <div className="modal-overlay" onClick={() => setShowTwilioModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Twilio Accounts</h2>

            <div style={{ marginBottom: 12 }}>
              <h3>Create Account</h3>
              <form onSubmit={handleCreateTwilioAccount}>
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" value={twilioForm.name} onChange={(e) => setTwilioForm({ ...twilioForm, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Account SID *</label>
                  <input type="text" value={twilioForm.accountSid} onChange={(e) => setTwilioForm({ ...twilioForm, accountSid: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Auth Token *</label>
                  <input type="text" value={twilioForm.authToken} onChange={(e) => setTwilioForm({ ...twilioForm, authToken: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Phone Numbers (comma separated)</label>
                  <input type="text" value={twilioForm.phoneNumbers} onChange={(e) => setTwilioForm({ ...twilioForm, phoneNumbers: e.target.value })} placeholder="+15550001, +15550002" />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="btn-primary">Create</button>
                  <button type="button" onClick={() => setTwilioForm({ name: '', accountSid: '', authToken: '', phoneNumbers: '' })} className="btn-cancel">Reset</button>
                </div>
              </form>
            </div>

            <div>
              <h3>Existing Accounts</h3>
              {twilioAccounts.length === 0 ? (
                <p>No accounts yet.</p>
              ) : (
                <ul>
                  {twilioAccounts.map(a => (
                    <li key={a.id} style={{ marginBottom: 8 }}>
                      <strong>{a.name}</strong> — {a.accountSid}
                      <div>Numbers: {a.phoneNumbers && a.phoneNumbers.join(', ')}</div>
                      <div style={{ marginTop: 6 }}>
                        <button className="btn-delete" onClick={() => handleDeleteTwilioAccount(a.id)}>Delete</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <button className="btn-cancel" onClick={() => setShowTwilioModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
